import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useData } from '../../contexts/DataContext';
import { toast } from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { automationAPI } from '../../services/api';

const { FiSave, FiMail, FiLink, FiZap, FiSettings, FiCopy, FiEye, FiEyeOff, FiBrain, FiCheckCircle } = FiIcons;

const AutomationSettings = () => {
  const { automationSettings, setAutomationSettings } = useData();
  const [formData, setFormData] = useState({
    emailParsing: automationSettings.emailParsing || false,
    webhookUrl: automationSettings.webhookUrl || '',
    zapierEnabled: automationSettings.zapierEnabled || false,
    autoArchiveDays: automationSettings.autoArchiveDays || 5,
    sendWelcomeEmail: automationSettings.sendWelcomeEmail || true,
    openaiApiKey: '',
    openaiModel: 'gpt-4o-mini',
    aiSystemPrompt: automationSettings.aiSystemPrompt || '',
    aiExcludedKeywords: automationSettings.aiExcludedKeywords || ''
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [apiKeyStatus, setApiKeyStatus] = useState(null);

  // Load settings from API
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setIsLoading(true);
        const settings = await automationAPI.getSettings();
        setFormData(prev => ({
          ...prev,
          ...settings,
          openaiApiKey: '' // Never load API key
        }));
        setApiKeyStatus(settings.openaiApiKey ? 'configured' : 'not-configured');
      } catch (error) {
        console.error('Error loading automation settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      await automationAPI.updateSettings(formData);
      setAutomationSettings(formData);
      if (formData.openaiApiKey) {
        setApiKeyStatus('configured');
      }
      toast.success('Automation settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.message || 'Failed to save automation settings');
    } finally {
      setIsSaving(false);
    }
  };

  const generateWebhookUrl = () => {
    const randomId = Math.random().toString(36).substring(2, 15);
    const webhookUrl = `https://api.rentalportal.com/webhook/${randomId}`;
    setFormData({ ...formData, webhookUrl });
  };

  const copyWebhookUrl = () => {
    if (formData.webhookUrl) {
      navigator.clipboard.writeText(formData.webhookUrl);
      toast.success('Webhook URL copied to clipboard!');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email Integration */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl shadow-sm p-8 border border-transparent">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiMail} className="text-blue-600 text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Email Integration</h2>
              <p className="text-sm text-gray-500">Manage automated email workflows</p>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
              <div>
                <h3 className="font-semibold text-gray-900">Auto-parse booking emails</h3>
                <p className="text-sm text-gray-500">Automatically create guest pages from confirmations</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" name="emailParsing" checked={formData.emailParsing} onChange={handleChange} className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
              <div>
                <h3 className="font-semibold text-gray-900">Send welcome emails</h3>
                <p className="text-sm text-gray-500">Automatically send portal access to guests</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" name="sendWelcomeEmail" checked={formData.sendWelcomeEmail} onChange={handleChange} className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        </motion.div>

        {/* Webhook Integration */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-3xl shadow-sm p-8 border border-transparent">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiLink} className="text-green-600 text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Webhook Integration</h2>
              <p className="text-sm text-gray-500">Connect to external services</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Webhook URL</label>
              <div className="flex space-x-2">
                <input 
                  type="url" 
                  name="webhookUrl" 
                  value={formData.webhookUrl} 
                  onChange={handleChange} 
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm bg-gray-50" 
                  placeholder="https://api.rentalportal.com/webhook/your-id" 
                />
                <button 
                  type="button" 
                  onClick={generateWebhookUrl} 
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors duration-200 font-medium"
                >
                  Generate
                </button>
                {formData.webhookUrl && (
                  <button type="button" onClick={copyWebhookUrl} className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-xl transition-colors duration-200">
                    <SafeIcon icon={FiCopy} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* AI/OpenAI Settings */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-3xl shadow-sm p-8 border border-transparent">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiBrain} className="text-purple-600 text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">AI Assistant (OpenAI)</h2>
              <p className="text-sm text-gray-500">Configure OpenAI for intelligent guest chat</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                OpenAI API Key
                {apiKeyStatus === 'configured' && (
                  <span className="ml-2 text-green-600 text-xs flex items-center">
                    <SafeIcon icon={FiCheckCircle} className="mr-1" />
                    Configured
                  </span>
                )}
              </label>
              <div className="relative">
                <input 
                  type={showApiKey ? 'text' : 'password'} 
                  name="openaiApiKey" 
                  value={formData.openaiApiKey} 
                  onChange={handleChange} 
                  className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm bg-gray-50" 
                  placeholder={apiKeyStatus === 'configured' ? '••••••••••••••••' : 'sk-...'} 
                />
                <button 
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={showApiKey ? FiEyeOff : FiEye} />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">platform.openai.com/api-keys</a>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">OpenAI Model</label>
              <select 
                name="openaiModel" 
                value={formData.openaiModel} 
                onChange={handleChange} 
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gray-50"
              >
                <optgroup label="Latest Models (2025)">
                  <option value="gpt-5.1">GPT-5.1 (Newest - Advanced reasoning & flexibility)</option>
                  <option value="gpt-5">GPT-5 (Reasoning, coding, agent-style tasks)</option>
                </optgroup>
                <optgroup label="GPT-4.1 Series">
                  <option value="gpt-4.1">GPT-4.1 (Multimodal - Text + Images)</option>
                  <option value="gpt-4.1-mini">GPT-4.1 Mini (Cost-efficient variant)</option>
                  <option value="gpt-4.1-nano">GPT-4.1 Nano (Lightweight variant)</option>
                </optgroup>
                <optgroup label="GPT-4 Series">
                  <option value="gpt-4o">GPT-4o (Optimized - More capable, higher cost)</option>
                  <option value="gpt-4o-mini">GPT-4o Mini (Recommended - Fast & Cost-effective)</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo (High capability)</option>
                  <option value="gpt-4">GPT-4 (Standard - Complex reasoning)</option>
                </optgroup>
                <optgroup label="Legacy Models">
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Lower cost, faster - Simple tasks)</option>
                </optgroup>
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Choose the model that best fits your needs. Newer models offer better capabilities but may have higher costs.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                AI System Prompt
              </label>
              <textarea
                name="aiSystemPrompt"
                value={formData.aiSystemPrompt || ''}
                onChange={handleChange}
                rows={8}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white font-mono text-sm"
                placeholder="Enter custom system prompt for the AI assistant. Leave empty to use default prompt."
              />
              <p className="text-xs text-gray-500 mt-2">
                Customize how the AI assistant behaves. Leave empty to use the default prompt.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Excluded Keywords
              </label>
              <textarea
                name="aiExcludedKeywords"
                value={formData.aiExcludedKeywords || ''}
                onChange={handleChange}
                rows={6}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white font-mono text-sm"
                placeholder="Enter keywords or topics the AI should avoid discussing (one per line or comma-separated)"
              />
              <p className="text-xs text-gray-500 mt-2">
                Enter words or phrases to exclude from generated content, one per line. These words will be automatically avoided in AI responses. Common examples: "discover", "unlock", "ultimate", "revolutionary", "game-changing", etc. You can add or remove any words as needed.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <motion.button 
            type="submit"
            disabled={isSaving || isLoading}
            whileHover={{ scale: 1.02 }} 
            whileTap={{ scale: 0.98 }} 
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-8 py-3 rounded-xl shadow-lg shadow-blue-200 transition-all duration-200 font-medium text-lg"
          >
            <SafeIcon icon={FiSave} />
            <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
          </motion.button>
        </div>
      </form>
    </div>
  );
};

export default AutomationSettings;